import type {
  CollaborationWorkspaceView,
  EntityDiscussion,
  GlobalFilters,
  NotificationFeed,
  QueryMetadata,
  QueryResult,
  WatchlistView,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * Comments, watches and notifications, from Postgres.
 *
 * These four screens were the last wholly authored ones in the product. The
 * collaboration register, the watchlist and the notification inbox rendered
 * from `packages/testing` — a comment somebody typed survived until they
 * pressed reload, and an unread count counted messages nobody had ever been
 * sent.
 *
 * The reads are on the `Repositories` port and swapped in `DataProvider`, like
 * every other screen's. The writes are not: that interface returns read models
 * only, and putting commands on it means deciding how commands are modelled
 * across the whole surface — a larger change than this cluster should force.
 * They live here beside the reads, behind the same `apiFetch`, so identity
 * travels the same way.
 */
export function createHttpCollaborationRepository(baseUrl: string) {
  return {
    async getCollaboration(
      filters: GlobalFilters,
    ): Promise<QueryResult<CollaborationWorkspaceView>> {
      const response = await apiFetch(`${baseUrl}/collaboration?${filterParams(filters)}`);
      if (!response.ok) throw new Error('The collaboration record could not be loaded.');
      return (await response.json()) as {
        data: CollaborationWorkspaceView;
        meta: QueryMetadata;
      };
    },

    async getWatchlist(): Promise<QueryResult<WatchlistView>> {
      const response = await apiFetch(`${baseUrl}/me/watchlist`);
      if (!response.ok) throw new Error('Your watchlist could not be loaded.');
      return (await response.json()) as { data: WatchlistView; meta: QueryMetadata };
    },

    /** The thread on one record. 404 is "not in your scope" and says nothing more. */
    async getDiscussion(entityId: string): Promise<EntityDiscussion | null> {
      const response = await apiFetch(`${baseUrl}/entities/${entityId}/comments`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('The discussion could not be loaded.');
      const body = (await response.json()) as { data: EntityDiscussion };
      return body.data;
    },

    async postComment(
      entityId: string,
      comment: { body: string; visibility?: string; mentionPersonIds?: string[] },
    ): Promise<{ ok: true } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/entities/${entityId}/comments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(comment),
      });
      if (response.ok) return { ok: true };
      // The server's message says *why* — too short, out of scope, already
      // settled — and a generic failure would throw the only useful part away.
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: body.message ?? `Request failed (${response.status}).` };
    },

    async resolveComment(
      commentId: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/comments/${commentId}/resolve`, {
        method: 'POST',
      });
      if (response.ok) return { ok: true };
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: body.message ?? `Request failed (${response.status}).` };
    },

    async getWatchState(entityId: string): Promise<{ watching: boolean; favourite: boolean }> {
      const response = await apiFetch(`${baseUrl}/entities/${entityId}/watch`);
      if (!response.ok) return { watching: false, favourite: false };
      const body = (await response.json()) as {
        data: { watching: boolean; favourite: boolean };
      };
      return body.data;
    },

    async setWatch(
      entityId: string,
      watching: boolean,
      kind: 'Watch' | 'Favourite' = 'Watch',
      reason?: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> {
      const response = watching
        ? await apiFetch(`${baseUrl}/entities/${entityId}/watch`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ kind, reason: reason ?? null }),
          })
        : await apiFetch(`${baseUrl}/entities/${entityId}/watch?kind=${kind}`, {
            method: 'DELETE',
          });
      if (response.ok) return { ok: true };
      const body = (await response.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: body.message ?? `Request failed (${response.status}).` };
    },

    async getNotifications(): Promise<NotificationFeed> {
      const response = await apiFetch(`${baseUrl}/me/notifications`);
      if (!response.ok) throw new Error('Your notifications could not be loaded.');
      const body = (await response.json()) as { data: NotificationFeed };
      return body.data;
    },

    /**
     * Marking read is a request, not a render.
     *
     * The returned feed is the server's, not a locally patched copy: a client
     * that decremented its own badge would disagree with the next reload the
     * first time two tabs were open.
     */
    async markNotificationRead(notificationId: string): Promise<NotificationFeed> {
      const response = await apiFetch(
        `${baseUrl}/me/notifications/${notificationId}/read`,
        { method: 'POST' },
      );
      if (!response.ok) throw new Error('That notification could not be marked read.');
      const body = (await response.json()) as { data: NotificationFeed };
      return body.data;
    },

    async markAllNotificationsRead(): Promise<NotificationFeed> {
      const response = await apiFetch(`${baseUrl}/me/notifications/read-all`, { method: 'POST' });
      if (!response.ok) throw new Error('Your notifications could not be marked read.');
      const body = (await response.json()) as { data: NotificationFeed };
      return body.data;
    },
  };
}
